import { IRowData } from "@multiweb/types/contracts";
import { ConditionParams, IDataSource, IDataSourceLoadInfo, IDataSourceLoadInfoBase, IOrderBy, IRecordsInfo } from "../../../Global/Base/DataSources/Interfaces";
import { Event } from "../../../Global/Base/Events";
import { NotImplementedException } from "../../../Global/Base/Exceptions";
import { IDisposable } from "../../../Global/Base/IDisposable";
import { CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { IActualObjectChangeEventArgs } from "./EditableDBDataSource";
/**
 * Datový zdroj pro obecné pole objektů
 */
export class ArrayDataSource<T extends object> implements IDataSource, IDisposable {
    protected _internalCollection: T[];
    protected _internalRows: IRowData[];

    protected _attrs: string[] = [];

    /**
     * Inicializuje datový zdroj
     * @param initialData Kolekce objektů, které má DS vracet.
     * @param dataKeyColumnName Název property, která se použije jako klíč objektu.
     * @param replacingAttrNames Jména atributu, které je potřeba přejmenovat, aby odpovídali attrName co jsou na objektu
     */
    public init(initialData: T[], dataKeyColumnName: string, _replacingAttrNames?: { originalName: string; newName: string }[]): void {
        this._internalCollection = initialData || [];
        this._internalRows = [];
        this._attrs = [];
        this.dataKey = "ID"; //Framework bohužel počítá s tím, že ID je přítomno v IRowData. (hledat data.ID), proto je potřeba držet stejnou konvenci a atribut ID přidat, pokud ho objekty nemají

        for (const obj of this._internalCollection) {
            const dr = {} as IRowData;
            for (const key in obj) {
                const validAttrName = key.toLocaleLowerCase(); //Od changesetu 35049[JB] jsou názvy sloupců automaticky převáděny na lowerCase. Ve starších verzích to zřejmě bude potřeba opravit bez lowerCase
                if (key === dataKeyColumnName && dataKeyColumnName !== "ID")
                    dr["ID"] = Reflect.get(obj, key).toString();
                if (!this._attrs.includes(validAttrName))
                    this._attrs.push(validAttrName);
                if (typeof obj[key] !== "function") {
                    dr[validAttrName] = Reflect.get(obj, key);
                }
            }
            this._internalRows.push(dr);
        }
    }

    //#region Events
    /**
     * Vraci startindex pro cteni dat
     */
    public dataLoaded: Event<IDataSource, IDataSourceLoadInfo> = new Event<IDataSource, IDataSourceLoadInfo>();

    public error: Event<ArrayDataSource<T>, any> = new Event<ArrayDataSource<T>, any>();

    /**
     * Pokud je zrušeno načítání dat
     */
    public loadingCancelled: Event<ArrayDataSource<T>, any> = new Event<ArrayDataSource<T>, IDataSourceLoadInfoBase>();

    /**
     * Pokud je spuštěno načítání dat
     */
    public loadingStarted: Event<ArrayDataSource<T>, any> = new Event<ArrayDataSource<T>, any>();

    /**
     * Pokud je ukončeno načítání dat
     */
    public loadingEnded: Event<ArrayDataSource<T>, IDataSourceLoadInfoBase> = new Event<ArrayDataSource<T>, IDataSourceLoadInfoBase>();

    /**
    * Vraci radek a zmeneny atribut
    */
    public readonly attributesChanged: Event<ArrayDataSource<T>, IActualObjectChangeEventArgs> = new Event<ArrayDataSource<T>, IActualObjectChangeEventArgs>();

    //#endregion Events

    //#region Fields

    public dataKey: string;
    public className: string;
    public orderBy: IOrderBy[];
    public condition: string;
    public conditionParams?: ConditionParams;
    //#endregion Fields

    /**
    * Vrací podmínku potřebnou pro načtení dat
    */
    public getOriginalCondition(): string {
        throw new NotImplementedException();
    }

    public getAttrs(): string[] {
        return this._attrs;
    }

    public getFullLengthAttrs(): string[] {
        return [];
    }

    public setAttrs(attrs: string[]): void {
        this._attrs = attrs;
    }
    public async setAttrsAndLoad(_attrs: string[], _cancellationToken?: CancellationToken): Promise<any> {
        return Promise.resolve();
    }

    /**
     * Smaže dočtené záznamy v datasoursu
     * */
    public clearData(): void {
        this._internalRows = [];
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: 0,
            dataGenerator: this._getIterator.bind(this, 0, 0),
            lastBatch: false,
            isFiltered: false,
            refreshedData: false,
            emptyData: true,
        };
        this.dataLoaded.trigger(this, dsLoadInfo);
    }

    public async loadNewData(keepSelectedRecords?: boolean, cancellationToken?: CancellationToken): Promise<IRowData[]> {
        this.loadingStarted.trigger(this, 0);
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: 0,
            dataGenerator: this._getIterator.bind(this, 0, this._internalRows.length),
            lastBatch: true,
            isFiltered: false/*this._filterCondition != ""*/,
            refreshedData: false,
            cancellationToken: cancellationToken,
            emptyData: false,
            keepSelectedRecords: keepSelectedRecords,
        };
        this.dataLoaded.trigger(this, dsLoadInfo);
        this.loadingEnded.trigger(this, dsLoadInfo);
        return this._internalRows;
    }
    public getNextData(_startIndex?: number, _count?: number, keepSelectedRecords?: boolean, cancellationToken?: CancellationToken): Promise<any> {
        return this.loadNewData(keepSelectedRecords, cancellationToken);
    }
    public refreshData(_fetchCount?: number, _manualRefresh?: boolean, _cancellationToken?: CancellationToken): Promise<any> {
        return Promise.resolve();
    }
    public waitForReload(_ct?: CancellationToken): Promise<void> {
        return Promise.resolve();
    }

    public [Symbol.iterator](): Iterator<IRowData> {
        if (!this._internalRows)
            this._internalRows = new Array<IRowData>();

        return this._internalRows[Symbol.iterator]();
    }
    public setFilterCondition(_condition: string): void {
        throw new NotImplementedException();
    }
    public getFilterCondition(): string {
        throw new NotImplementedException();
    }
    /**
     * Vrací true, pokud je datasource filtrován.
     */
    public get hasFilterCondition(): boolean {
        return false;
    }
    protected *_getIterator(startIndex: number, count?: number): IterableIterator<IRowData> {
        const maxIndex = count ? Math.min(startIndex + count, this._internalRows.length) : this._internalRows.length;
        for (let i = startIndex; i < maxIndex; i++)
            yield this._internalRows[i];
    }

    public dispose(): void {
        // Prázdná metoda
    }

    /**
     * Metoda pro dočtení jednoho určitého záznamu
     * @param _dataKey Datový klíč (většinou ID) k načtení
     * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
     */
    public async loadSpecificRecord(_dataKey: string, _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData> {
        throw new NotImplementedException();
    }

    /**
    * Metoda pro dočtení určitých záznamů
    * @param _dataKeys Pole datových klíčů (většinou ID) k načtení
    * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    public async loadSpecificRecords(_dataKeys: string[], _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        throw new NotImplementedException();
    }

    /*
     * Vrací počet zadaných záznamů pro aktuálně nastavený filtr
     */
    public async count(): Promise<IRecordsInfo> {
        return {
            isFiltered: false,
            totalCount: this._internalRows.length,
        };
    }
}

export class ArrayMSLOVDataSource<T extends object> extends ArrayDataSource<T> {
    /**
     * Inicializuje datový zdroj
     * @param initialData Kolekce objektů, které má DS vracet.
     * @param dataKeyColumnName Název property, která se použije jako klíč objektu.
     * @param replacingAttrNames Jména atributu, které je potřeba přejmenovat, aby odpovídali attrName co jsou na objektu
     */
    public override init(initialData: T[] | undefined, dataKeyColumnName: string, replacingAttrNames?: { originalName: string; newName: string }[]): void {
        this._internalCollection = initialData || [];
        this._internalRows = [];
        this._attrs = [];
        this.dataKey = "ID"; //Framework bohužel počítá s tím, že ID je přítomno v IRowData. (hledat data.ID), proto je potřeba držet stejnou konvenci a atribut ID přidat, pokud ho objekty nemají

        for (const obj of this._internalCollection) {
            const dataRow = {} as IRowData;
            for (const key in obj) {
                let validAttrName = key.toLocaleLowerCase(); //Od changesetu 35049[JB] jsou názvy sloupců automaticky převáděny na lowerCase. Ve starších verzích to zřejmě bude potřeba opravit bez lowerCase

                if (replacingAttrNames) {
                    const replacingAttrName = replacingAttrNames.find(e => e.originalName === validAttrName);
                    validAttrName = replacingAttrName ? replacingAttrName.newName : validAttrName;
                }
                if (key === dataKeyColumnName && dataKeyColumnName !== "ID")
                    dataRow["ID"] = Reflect.get(obj, key).toString();
                if (!this._attrs.includes(validAttrName))
                    this._attrs.push(validAttrName);
                if (typeof obj[key] !== "function") {
                    dataRow[validAttrName] = Reflect.get(obj, key);
                }
            }

            this._internalRows.push({ preselect: dataRow.active, data: dataRow });
        }
    }
}
